import type { ReactNode } from "react";

export type BadgeVariant =
  | "default"
  | "outline"
  | "asset-prompt"
  | "asset-app"
  | "asset-tool"
  | "asset-agent"
  | "asset-template"
  | "complexity-beginner"
  | "complexity-intermediate"
  | "complexity-advanced"
  | "status-approved"
  | "status-pilot"
  | "status-review"
  | "status-blocked"
  | "priority-high"
  | "priority-medium"
  | "priority-low"
  | "score-high"
  | "score-medium"
  | "score-low"
  | "sensitivity-private"
  | "sensitivity-public"
  | "sensitivity-pending";

type Props = {
  children: ReactNode;
  variant?: BadgeVariant;
  withDot?: boolean;
  title?: string;
};

const classFor = (variant: BadgeVariant): string => {
  switch (variant) {
    case "outline": return "badge badge-outline";
    case "asset-prompt": return "badge badge-asset-prompt";
    case "asset-app": return "badge badge-asset-app";
    case "asset-tool": return "badge badge-asset-tool";
    case "asset-agent": return "badge badge-asset-agent";
    case "asset-template": return "badge badge-asset-template";
    case "complexity-beginner": return "badge badge-complexity-beginner";
    case "complexity-intermediate": return "badge badge-complexity-intermediate";
    case "complexity-advanced": return "badge badge-complexity-advanced";
    case "status-approved": return "badge badge-status-approved";
    case "status-pilot": return "badge badge-status-pilot";
    case "status-review": return "badge badge-status-review";
    case "status-blocked": return "badge badge-status-blocked";
    case "priority-high": return "badge badge-priority-high";
    case "priority-medium": return "badge badge-priority-medium";
    case "priority-low": return "badge badge-priority-low";
    case "score-high": return "badge badge-score-high";
    case "score-medium": return "badge badge-score-medium";
    case "score-low": return "badge badge-score-low";
    case "sensitivity-private": return "badge badge-sensitivity-private";
    case "sensitivity-public": return "badge badge-sensitivity-public";
    case "sensitivity-pending": return "badge badge-sensitivity-pending";
    default: return "badge";
  }
};

export function Badge({ children, variant = "default", withDot = false, title }: Props) {
  return (
    <span className={classFor(variant)} title={title}>
      {withDot ? <span className="dot" /> : null}
      {children}
    </span>
  );
}

export function assetVariant(assetType: string): BadgeVariant {
  const v = assetType.toLowerCase();
  if (v.includes("prompt")) return "asset-prompt";
  if (v.includes("app")) return "asset-app";
  if (v.includes("agent")) return "asset-agent";
  if (v.includes("tool")) return "asset-tool";
  if (v.includes("template")) return "asset-template";
  return "default";
}

export function complexityVariant(level: string): BadgeVariant {
  const v = level.toLowerCase();
  if (v.startsWith("beg")) return "complexity-beginner";
  if (v.startsWith("int")) return "complexity-intermediate";
  if (v.startsWith("adv")) return "complexity-advanced";
  return "outline";
}

export function statusVariant(status: string): BadgeVariant {
  const v = status.toLowerCase();
  
  // Green (Good / Live / Done)
  if (v.includes("approved") || v.includes("published") || v.includes("live")) {
    return "status-approved";
  }
  
  // Red (Blocked / Bad / Dead)
  if (v.includes("blocked") || v.includes("rejected") || v.includes("cancelled") || v.includes("archive") || v.includes("forbidden")) {
    return "status-blocked";
  }
  
  // Gold (In Progress / Active Work)
  if (v.includes("pilot") || v.includes("design") || v.includes("development") || v.includes("testing") || v.includes("progress")) {
    return "status-pilot";
  }

  // Yellow (Pending / Queue / Backlog / New / Under Review / awaiting input)
  if (
    v.includes("review") ||
    v.includes("new") ||
    v.includes("backlog") ||
    v.includes("pending") ||
    v.includes("idea") ||
    v.includes("required") // e.g. "Info Required" — waiting on someone to act
  ) {
    return "status-review";
  }

  return "outline";
}

/** Coarse semantic bucket derived from statusVariant — the single source of
 *  truth other pages (e.g. Insights' approved/pending stats) should use
 *  instead of keeping their own separate status-matching regexes, which
 *  drift out of sync with the badge coloring and with each other. */
export type StatusCategory = "approved" | "blocked" | "inProgress" | "pending" | "other";

export function statusCategory(status: string): StatusCategory {
  switch (statusVariant(status)) {
    case "status-approved": return "approved";
    case "status-blocked": return "blocked";
    case "status-pilot": return "inProgress";
    case "status-review": return "pending";
    default: return "other";
  }
}

export function scoreVariant(score: number): BadgeVariant {
  if (score >= 8) return "score-high";
  if (score >= 5) return "score-medium";
  return "score-low";
}

export function priorityVariant(priority: string): BadgeVariant {
  const v = priority.toLowerCase();
  if (v.includes("high")) return "priority-high";
  if (v.includes("med")) return "priority-medium";
  if (v.includes("low")) return "priority-low";
  return "outline";
}